import React, {useEffect, useMemo, useRef} from 'react';
import {Mesh} from 'three';
import {useThree} from '@react-three/fiber';
import {useFrameRenderer} from 'hooks';
import {useARStore} from 'stores';

interface IARCoreCompositeProps {}

const ARCoreComposite = ({}: IARCoreCompositeProps) => {
  const compositeMeshRef = useARStore(s => s.compositeMeshRef);
  const hackMeshRef = useRef<Mesh>(null);
  const camera = useThree(s => s.camera);

  useFrameRenderer({compositeMeshRef});

  // Using clip space [-1, 1]
  const vertexShader = useMemo(() => {
    return `
      varying vec2 v_uv;
      void main() {
        v_uv = uv;
        gl_Position = vec4(position.xy, 0.0, 1.0);
      }
    `;
  }, []);

  const fragmentShader = useMemo(() => {
    return `
      uniform sampler2D u_camera;
      uniform sampler2D u_scene;
      varying vec2 v_uv;
      void main() {
        vec4 camera_color = texture2D(u_camera, v_uv);
        vec4 scene_color = texture2D(u_scene, v_uv);
        scene_color = sRGBTransferOETF(scene_color); // to ensure correct color for fbo

        // To replicate WebXR colors
        vec3 adjusted_scene = scene_color.rgb;
        if(scene_color.a > 0.01) {
          // Contrast 125% (pivot around 0.5 to avoid shifting brightness)
          adjusted_scene = (adjusted_scene - 0.5) * 1.25 + 0.5;
          // Brightness 110%
          adjusted_scene *= 1.1;
          // Saturation 85% (convert to grayscale, then mix back)
          float luma = dot(adjusted_scene, vec3(0.299, 0.587, 0.114));
          adjusted_scene = mix(vec3(luma), adjusted_scene, 0.85);
        }
        gl_FragColor = vec4(camera_color.rgb * (1.0 - scene_color.a) + adjusted_scene * scene_color.a, max(scene_color.a, camera_color.a));
      }
    `;
  }, []);

  const uniforms = useMemo(() => {
    return {
      u_camera: {value: null},
      u_scene: {value: null},
    };
  }, []);

  useEffect(() => {
    if (compositeMeshRef.current && hackMeshRef.current) {
      compositeMeshRef.current.parent = camera;
      hackMeshRef.current.parent = camera;
      hackMeshRef.current.position.z = -10;
    }
  }, []);

  return (
    <>
      <mesh ref={compositeMeshRef} renderOrder={999}>
        {/* https://threejs.org/docs/#api/en/materials/ShaderMaterial */}
        <shaderMaterial vertexShader={vertexShader} fragmentShader={fragmentShader} uniforms={uniforms} />
        {/* [2, 2] as clip space goes from -1 to 1 */}
        <planeGeometry args={[2, 2]} />
      </mesh>
      {/* There is an issue in xr that a meshStandardMaterial is required to be present */}
      {/* in the camera view all time for the scene to render properly in the composite mesh  */}
      <mesh ref={hackMeshRef} scale={0.001}>
        <meshStandardMaterial />
      </mesh>
    </>
  );
};

export default React.memo(ARCoreComposite);
